import React, { ReactNode, Fragment } from "react";

// Types for parsing tokens
interface OpeningTag {
  type: 'opening';
  tag: string;
  props: Record<string, string>;
}

interface ClosingTag {
  type: 'closing';
  tag: string;
}

type ParseToken = string | ReactNode | OpeningTag | ClosingTag;

/**
 * Safely parses HTML elements from a string and converts them to JSX
 * Supports common HTML elements while preventing XSS attacks
 */
export const jsxParser = (htmlString: string): ReactNode => {
  if (!htmlString || typeof htmlString !== 'string') {
    return htmlString;
  }

  // Define allowed HTML elements and their React equivalents
  const allowedElements: Record<string, boolean> = {
    'strong': true,
    'b': true,
    'em': true,
    'i': true,
    'u': true,
    'span': true,
    'div': true,
    'p': true,
    'br': true,
    'a': true,
    'code': true,
    'pre': true,
  };

  const parseAttributes = (attrString: string): Record<string, string> => {
    const attributes: Record<string, string> = {};
    if (!attrString?.trim()) return attributes;

    // Parse attributes more robustly
    const attrRegex = /(\w+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
    let match;
    
    while ((match = attrRegex.exec(attrString)) !== null) {
      const [, key, doubleQuoted, singleQuoted, unquoted] = match;
      const value = doubleQuoted || singleQuoted || unquoted || '';
      
      // Only allow safe attributes
      if (['href', 'title', 'class', 'className', 'style', 'target', 'rel'].includes(key.toLowerCase())) {
        const safeKey = key === 'class' ? 'className' : key;
        attributes[safeKey] = value;
      }
    }
    
    return attributes;
  };

  const createReactElement = (tagName: string, props: any, children?: ReactNode): ReactNode => {
    // Generate unique keys for React elements
    const key = Math.random().toString(36).substr(2, 9);
    const elementProps = { ...props, key };

    switch (tagName.toLowerCase()) {
      case 'strong': return <strong {...elementProps}>{children}</strong>;
      case 'b': return <b {...elementProps}>{children}</b>;
      case 'em': return <em {...elementProps}>{children}</em>;
      case 'i': return <i {...elementProps}>{children}</i>;
      case 'u': return <u {...elementProps}>{children}</u>;
      case 'span': return <span {...elementProps}>{children}</span>;
      case 'div': return <div {...elementProps}>{children}</div>;
      case 'p': return <p {...elementProps}>{children}</p>;
      case 'br': return <br {...elementProps} />;
      case 'code': return <code {...elementProps}>{children}</code>;
      case 'pre': return <pre {...elementProps}>{children}</pre>;
      case 'a': {
        const href = props?.href;
        if (href) {
          return (
            <a 
              {...elementProps} 
              href={href}
              target={props.target || "_blank"} 
              rel={props.rel || "noopener noreferrer"}
            >
              {children}
            </a>
          );
        }
        return <span {...elementProps}>{children}</span>;
      }
      default:
        return <span {...elementProps}>{children}</span>;
    }
  };

  const parseHtmlRecursive = (str: string): ParseToken[] => {
    const result: ParseToken[] = [];
    const regex = /<(\/?)([\w]+)([^>]*)>/g;
    let lastIndex = 0;
    let match;
    
    while ((match = regex.exec(str)) !== null) {
      const [fullMatch, isClosing, tagName, attributes] = match;
      const textBefore = str.slice(lastIndex, match.index);
      
      // Add text content before the tag
      if (textBefore) {
        result.push(textBefore);
      }
      
      const lowerTagName = tagName.toLowerCase();
      
      if (allowedElements[lowerTagName]) {
        if (isClosing) {
          // This is a closing tag
          result.push({ type: 'closing', tag: lowerTagName });
        } else {
          // This is an opening tag
          const props = parseAttributes(attributes);
          
          if (lowerTagName === 'br') {
            // Self-closing tag
            result.push(createReactElement(lowerTagName, props));
          } else {
            result.push({ type: 'opening', tag: lowerTagName, props });
          }
        }
      } else {
        // Unknown tag - treat as text
        result.push(fullMatch);
      }
      
      lastIndex = regex.lastIndex;
    }
    
    // Add remaining text
    if (lastIndex < str.length) {
      result.push(str.slice(lastIndex));
    }
    
    return result;
  };

  const isOpeningTag = (token: ParseToken): token is OpeningTag => {
    return typeof token === 'object' && token !== null && !React.isValidElement(token) && 'type' in token && token.type === 'opening';
  };

  const isClosingTag = (token: ParseToken): token is ClosingTag => {
    return typeof token === 'object' && token !== null && !React.isValidElement(token) && 'type' in token && token.type === 'closing';
  };

  const buildJSXTree = (tokens: ParseToken[]): ReactNode[] => {
    const result: ReactNode[] = [];
    const stack: Array<{ tag: string; props: any; content: ReactNode[] }> = [];
    
    for (const token of tokens) {
      if (typeof token === 'string') {
        if (stack.length > 0) {
          stack[stack.length - 1].content.push(token);
        } else {
          result.push(token);
        }
      } else if (React.isValidElement(token)) {
        if (stack.length > 0) {
          stack[stack.length - 1].content.push(token);
        } else {
          result.push(token);
        }
      } else if (isOpeningTag(token)) {
        stack.push({ tag: token.tag, props: token.props, content: [] });
      } else if (isClosingTag(token) && stack.length > 0) {
        const current = stack[stack.length - 1];
        if (current.tag === token.tag) {
          const completed = stack.pop()!;
          const element = createReactElement(
            completed.tag, 
            completed.props, 
            completed.content.length === 0 ? null : 
            completed.content.length === 1 ? completed.content[0] : 
            <Fragment>{completed.content}</Fragment>
          );
          
          if (stack.length > 0) {
            stack[stack.length - 1].content.push(element);
          } else {
            result.push(element);
          }
        }
      }
    }
    
    // Handle unclosed tags
    while (stack.length > 0) {
      const unclosed = stack.pop()!;
      const element = createReactElement(
        unclosed.tag, 
        unclosed.props, 
        unclosed.content.length === 0 ? null : 
        unclosed.content.length === 1 ? unclosed.content[0] : 
        <Fragment>{unclosed.content}</Fragment>
      );
      result.push(element);
    }
    
    return result;
  };

  try {
    const tokens = parseHtmlRecursive(htmlString);
    const jsxElements = buildJSXTree(tokens);
    
    if (jsxElements.length === 0) {
      return null;
    } else if (jsxElements.length === 1) {
      return jsxElements[0];
    } else {
      return <Fragment>{jsxElements}</Fragment>;
    }
  } catch (error) {
    // Fallback to plain text if parsing fails
    console.warn('JSX Parser error:', error);
    return htmlString;
  }
};

